import { Center, Group, MantineStyleProps, useMatches } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { AuctionDescription } from "../AuctionDescription";
import { TransactionTableRow } from "./components";

import {
  EmptyState,
  EmptyStateRevamped,
  ListTable,
  ListTableColumnSortBy,
  ListTableTh,
  Modal,
  useColumnSorter,
} from "@/components/elements";
import PopoverTable from "@/components/elements/PopoverTable/PopoverTable";
import { IconFileText } from "@/components/icons";

import type { AuctionCarTransaction, AuctionCarTransactionColumns } from "@/types/auctions";
import type { FC } from "react";

export type TransactionsTableProps = {
  columns?: AuctionCarTransactionColumns[];
  columnsDisabled?: AuctionCarTransactionColumns[];
  hasActiveFilters?: boolean;
  namespace?: string;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSearch?: (query: string) => void;
  searchValue?: string;
  suppressEmptyState?: boolean;
  page?: number;
  pageSize?: number;
  totalTransactions?: number;
  transactions?: AuctionCarTransaction[];
  transactionDetailsTitle?: string;
  marketCarId?: string;
  auctionId?: string;
} & MantineStyleProps;

export const TransactionsTable: FC<TransactionsTableProps> = ({
  columns = [],
  columnsDisabled,
  hasActiveFilters = false,
  namespace = "transactions-table",
  onPageChange,
  onPageSizeChange,
  onSearch,
  searchValue,
  suppressEmptyState = false,
  page = 1,
  pageSize = 25,
  totalTransactions = 0,
  transactions = [],
  transactionDetailsTitle = "components.TransactionsTable.transaction_details",
  marketCarId,
  auctionId,
}) => {
  const { t } = useTranslation();
  const [opened, { open, close }] = useDisclosure();
  const [currentTransaction, setCurrentTransaction] = useState<AuctionCarTransaction>();
  const { getSortById, getUpdateSort } = useColumnSorter(`${namespace}-sort`);
  const [popoverOpened, setPopoverOpened] = useState(false);
  const isMobile = useMatches({ base: true, sm: false });

  const isSearching = Boolean(searchValue?.trim());
  const hasData = totalTransactions > 0;

  const tableColumns: ListTableTh[] = columns.map((id) => ({
    id,
    label: t(`components.TransactionsTable.header.${id}`, id),
    sorter: getColumnSorter(id),
    thProps: {
      miw: "max-content",
      style: { whiteSpace: "nowrap", width: "1%" },
    },
  }));

  const hasPageSizeChanger = !!onPageSizeChange && totalTransactions > 10;
  const hasPagination = !!onPageChange && totalTransactions > pageSize;

  if (!hasData && suppressEmptyState && !isSearching) {
    return null;
  }

  function getColumnSorter(id: AuctionCarTransactionColumns) {
    return id !== "more" && id !== "outlier" && id !== "replica" ? (
      <ListTable.ColumnSorter
        tooltip={t("components.TransactionsTable.sort_column")}
        sortBy={getSortById(id)}
        onSortChange={onChangeSortBy(id)}
      />
    ) : undefined;
  }

  function onChangeSortBy(id: AuctionCarTransactionColumns) {
    return (sortBy?: ListTableColumnSortBy) => {
      getUpdateSort(id)(sortBy);
    };
  }

  function onTransactionMoreClick(transaction: AuctionCarTransaction) {
    return () => {
      setCurrentTransaction(transaction);
      open();
    };
  }

  return (
    <>
      <ListTable.Context columns={tableColumns} disabled={columnsDisabled} namespace={namespace}>
        {({ filteredColumns, headingColumns, onFilteredColumnsChange }) => {
          const fixedMoreColumn = headingColumns.find(({ id }) => id === "more");

          const organizedHeadingColumns = headingColumns.filter(({ id }) => id !== "more");

          const columnsForTable = fixedMoreColumn
            ? [...organizedHeadingColumns, fixedMoreColumn]
            : organizedHeadingColumns;

          return hasData || isSearching ? (
            <>
              <Group gap="md" py="lg">
                {onSearch && (
                  <ListTable.SearchInput
                    onChange={onSearch}
                    placeholder={t("components.TransactionsTable.search_placeholder")}
                    resetTooltip={t("components.TransactionsTable.reset_tooltip")}
                    updateOnChange
                    value={searchValue}
                  />
                )}

                {isMobile ? (
                  <PopoverTable
                    popoverOpened={popoverOpened}
                    setPopoverOpened={() => setPopoverOpened((prev) => !prev)}
                    actionInPending={false}
                    filteredColumns={filteredColumns}
                    onFilteredColumnsChange={onFilteredColumnsChange}
                  />
                ) : (
                  <ListTable.ColumnsOrganizer
                    columns={filteredColumns}
                    label={t("common.customizeColumns")}
                    onChange={onFilteredColumnsChange}
                    tooltip={t("common.organizeColumns")}
                  />
                )}
              </Group>

              {hasData ? (
                <>
                  <ListTable
                    data={{
                      head: columnsForTable,
                      body: transactions.map((transaction, index) => (
                        <TransactionTableRow
                          key={index}
                          columns={
                            columnsForTable.map(({ id }) => id) as AuctionCarTransactionColumns[]
                          }
                          data={transaction}
                          onMoreClick={onTransactionMoreClick(transaction)}
                        />
                      )),
                    }}
                  />

                  {(hasPageSizeChanger || hasPagination) && (
                    <Group justify="flex-end" p="lg">
                      {hasPageSizeChanger && (
                        <ListTable.PageSizeChanger
                          label={t("components.ListTable.page_size_changer_label")}
                          onChange={onPageSizeChange}
                          pageSize={pageSize}
                        />
                      )}
                      {hasPagination && (
                        <ListTable.Pagination
                          divider={t("components.ListTable.pagination_divider")}
                          page={page}
                          pageSize={pageSize}
                          onChange={onPageChange}
                          total={totalTransactions}
                        />
                      )}
                    </Group>
                  )}
                </>
              ) : suppressEmptyState ? null : (
                <Center h="18em">
                  <EmptyStateRevamped />
                </Center>
              )}
            </>
          ) : (
            <EmptyState
              type={hasActiveFilters || isSearching ? "noResults" : "noData"}
              text={
                hasActiveFilters || isSearching
                  ? t("components.TransactionsTable.no_results", {
                      defaultValue: t("common.noData"),
                    })
                  : t("common.noData")
              }
            />
          );
        }}
      </ListTable.Context>

      <Modal
        opened={opened}
        onClose={close}
        icon={IconFileText}
        title={t(transactionDetailsTitle)}
        size="lg"
      >
        <AuctionDescription
          auctionId={auctionId}
          currentTransaction={currentTransaction}
          marketCarId={marketCarId}
        />
      </Modal>
    </>
  );
};
